import React, { useContext, useRef, useState } from 'react';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ViewContext } from 'components/lib';
import { UMButton } from '@urbanmetrix/um-kit';
import {
  deleteProjectImportedFile,
  getProjectImportedFile,
  getProjectImportedFiles,
  uploadProjectImportedFile,
  updateProjectImportedFile,
} from '../../api/projectApi';
import { getApiErrorMessage } from '../../api/apiClient';
import GridFilesPanel from './GridFilesPanel';
import { triggerBlobDownload } from './fileDownload';

type ImportedFileProps = {
  projectId: number | string | null;
  showDeletedItems?: boolean;
};

const TYPE_METADATA = {
  customScene: {
    icon: ViewInArIcon,
    label: 'Custom Scene',
  },
  customObstruction: {
    icon: ViewInArIcon,
    label: 'Custom Obstruction',
  },
  trees: {
    icon: InsertDriveFileIcon,
    label: 'Trees',
  },
  projectSketches: {
    icon: InsertDriveFileIcon,
    label: 'Project Sketches',
  },
};

const ImportedFile: React.FC<ImportedFileProps> = ({ projectId, showDeletedItems = false }) => {
  const context = useContext(ViewContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<'customObstruction' | 'projectSketches'>('customObstruction');
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState<HTMLElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const selectKindAndOpenPicker = (selectedKind: typeof kind) => {
    setKind(selectedKind);
    setUploadMenuAnchor(null);
    fileInputRef.current?.click();
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !projectId) return;

    if (!file.name.toLowerCase().endsWith('.obj')) {
      context.notification.show('Only .obj files are allowed', 'error', true, 'toast');
      return;
    }

    setIsUploading(true);
    try {
      await uploadProjectImportedFile(String(projectId), kind, file);
      setReloadKey((current) => current + 1);
      context.notification.show(`${file.name} was uploaded`, 'success', true, 'toast');
    } catch (error) {
      context.notification.show(getApiErrorMessage(error, 'Unable to upload file'), 'error', true, 'toast');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <GridFilesPanel
      title="Imported Files"
      currentGridId={projectId}
      listEmptyMessage="No imported files found."
      filteredEmptyMessage="No imported files match the selected types."
      dateLabel="Imported"
      loadFiles={getProjectImportedFiles}
      loadFileDetail={getProjectImportedFile}
      renameFile={updateProjectImportedFile}
      deleteFile={deleteProjectImportedFile}
      getFileType={(file) => file?.kind ?? file?.type ?? ''}
      typeMetadata={TYPE_METADATA}
      reloadKey={reloadKey}
      showDeletedFiles={showDeletedItems}
      headerActions={
        <>
          <UMButton
            text={isUploading ? 'Uploading…' : 'Upload .obj'}
            variant="outlined"
            disabled={isUploading || !projectId}
            onClick={(event: any) => setUploadMenuAnchor(event.currentTarget)}
            style={{ minWidth: '120px', paddingInline: '14px' }}
          />
          <Menu anchorEl={uploadMenuAnchor} open={Boolean(uploadMenuAnchor)} onClose={() => setUploadMenuAnchor(null)}>
            <MenuItem onClick={() => selectKindAndOpenPicker('customObstruction')}>Custom Obstruction</MenuItem>
            <MenuItem onClick={() => selectKindAndOpenPicker('projectSketches')}>Project Sketch</MenuItem>
          </Menu>
          <input ref={fileInputRef} hidden type="file" accept=".obj" onChange={handleUpload} />
        </>
      }
      downloadFile={async (_projectId, file, detail) => {
        const downloadUrl = detail?.downloadUrl;
        if (!downloadUrl) throw new Error('Download URL missing');

        const response = await fetch(downloadUrl, { credentials: 'include' });
        if (!response.ok) throw new Error(`Failed to download file: ${response.status}`);

        triggerBlobDownload(await response.blob(), detail?.name || file.name || 'download');
      }}
    />
  );
};

export default ImportedFile;
